import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check } from 'lucide-react';
import MarqueeBar from '../components/MarqueeBar';
import Accordion from '../components/Accordion';
import Stars from '../components/Stars';
import SEO from '../components/SEO';
import {
  HERO,
  STORY_BLOCKS,
  STORY_IMAGE,
  STORY_IMAGE_2,
  QUOTE_REVIEW,
  TESTIMONIALS,
  HOME_FAQS,
} from '../mock';

const HeroSection = () => (
  <section className="relative w-full overflow-hidden" style={{ minHeight: '600px' }}>
    <img
      src={HERO.image}
      alt="K-Root model"
      className="absolute inset-0 w-full h-full object-cover"
      style={{ objectPosition: 'center right' }}
    />
    <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(245,237,223,0.65) 0%, rgba(245,237,223,0.15) 50%, transparent 75%)' }} />
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 lg:py-40">
      <div className="max-w-[560px] fade-up">
        <h1
          className="font-serif text-[44px] md:text-[60px] lg:text-[68px] leading-[1.05] tracking-tight"
          style={{ color: '#2D5A3D' }}
        >
          {HERO.heading}
        </h1>
        <p className="mt-6 text-[15px] md:text-[17px] leading-relaxed max-w-[460px]" style={{ color: '#2D5A3D' }}>
          {HERO.subheading}
        </p>
        <Link
          to="/products/korean-scalp-revival-bar"
          className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full text-[13px] uppercase tracking-[0.2em] font-medium btn-hover"
          style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}
        >
          {HERO.cta}
          <ArrowUpRight size={16} />
        </Link>
      </div>
    </div>
  </section>
);

const StorySection = () => (
  <section className="py-20 md:py-28" style={{ backgroundColor: '#F5EDDF' }}>
    <div className="max-w-[1280px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
      <div className="order-2 md:order-1">
        <img src={STORY_IMAGE} alt="K-Root product" className="w-full h-auto rounded-2xl" />
      </div>
      <div className="order-1 md:order-2">
        <h2 className="font-serif text-[34px] md:text-[44px] leading-[1.1]" style={{ color: '#2D5A3D' }}>
          Your Hair Did Not Quit. Your Scalp Did.
        </h2>
        <p className="mt-5 text-[15px] leading-relaxed" style={{ color: '#2D5A3D' }}>
          The strands are not the problem — the soil under them is. Years of sulfates, silicones and silent inflammation choked the follicle until growth simply stopped. K-Root is the Korean reset: 60 seconds of fermented herbs that clear the buildup, wake the root and put you back in charge of your hair.
        </p>
        <div className="mt-8">
          <Accordion items={STORY_BLOCKS.map((s) => ({ q: s.title, a: s.body }))} />
        </div>
      </div>
    </div>
  </section>
);

const QuoteSection = () => (
  <section className="py-20 md:py-24" style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}>
    <div className="max-w-[820px] mx-auto px-6 md:px-12 text-center">
      <Stars rating={5} size={18} color="#E2BF6E" />
      <h3 className="font-serif italic text-[34px] md:text-[44px] mt-5 leading-tight">
        {QUOTE_REVIEW.text}
      </h3>
      <p className="mt-6 text-[15px] leading-relaxed opacity-90">{QUOTE_REVIEW.body}</p>
      <p className="mt-6 text-[13px] tracking-[0.18em] uppercase opacity-80">— {QUOTE_REVIEW.author}</p>
    </div>
  </section>
);

const AboutSection = () => (
  <section className="py-20 md:py-28" style={{ backgroundColor: '#F5EDE0' }}>
    <div className="max-w-[1280px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center">
      <div>
        <h2 className="font-serif text-[34px] md:text-[44px] leading-[1.1]" style={{ color: '#2D5A3D' }}>
          We Built K-Root Because Nothing Else Worked.
        </h2>
        <p className="mt-5 text-[15px] leading-relaxed" style={{ color: '#2D5A3D' }}>
          After spending thousands on serums, supplements and clinic visits that promised everything and delivered a slightly fuller wallet — for the clinic — we got blunt. Korean women have treated the scalp first for centuries and Western women have been ignored for decades. So we built the bar we wished existed: zero filler, zero subscription trap, zero pharmaceutical risk. One triangle, 12 fermented herbs, 60 seconds in your shower. No miracle claims. Just biology, finally aimed at the right target.
        </p>
        <Link
          to="/products/korean-scalp-revival-bar"
          className="mt-8 inline-flex items-center gap-2 text-[14px] tracking-[0.15em] uppercase border-b pb-1 btn-hover"
          style={{ color: '#2D5A3D', borderColor: '#2D5A3D' }}
        >
          See What Makes Us Different
          <ArrowUpRight size={16} />
        </Link>
      </div>
      <div>
        <img src={STORY_IMAGE_2} alt="K-Root lifestyle" className="w-full h-auto rounded-2xl" />
      </div>
    </div>
  </section>
);

const RecognizeSection = () => (
  <section className="py-20 md:py-28" style={{ backgroundColor: '#F5EDDF' }}>
    <div className="max-w-[820px] mx-auto px-6 md:px-12 text-center">
      <h2 className="font-serif text-[34px] md:text-[46px] leading-[1.1]" style={{ color: '#2D5A3D' }}>
        Stop Counting Strands. Start Counting Wins.
      </h2>
      <p className="mt-6 text-[15px] leading-relaxed" style={{ color: '#2D5A3D' }}>
        The lighting in every selfie. The brush full of hair. The hat that never leaves your bag. The mirror in the morning where you pretend not to notice the part getting wider. You have been carrying this quietly for too long. K-Root is the line in the sand — the first ritual that hits the actual cause of the loss, not the symptom. Three weeks from now your drain looks different. Eight weeks from now your reflection does too.
      </p>
      <p className="mt-5 font-serif italic text-[22px] md:text-[26px]" style={{ color: '#2D5A3D' }}>
        The version of you with hair you love is not gone. She is one bar away.
      </p>
      <Link
        to="/products/korean-scalp-revival-bar"
        className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full text-[13px] uppercase tracking-[0.2em] font-medium btn-hover"
        style={{ backgroundColor: '#2D5A3D', color: '#F5EDDF' }}
      >
        Start the 60-Second Ritual
        <ArrowUpRight size={16} />
      </Link>
    </div>
  </section>
);

const TestimonialsSection = () => (
  <section className="py-20 md:py-28 bg-white">
    <div className="max-w-[1280px] mx-auto px-6 md:px-12">
      <h2 className="font-serif text-[32px] md:text-[40px] mb-12 text-center" style={{ color: '#2D5A3D' }}>
        10,000+ women. One ritual. Real regrowth.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {TESTIMONIALS.map((t) => (
          <article key={t.id} className="flex flex-col rounded-2xl overflow-hidden border"
            style={{ borderColor: 'rgba(45,90,61,0.12)', backgroundColor: '#F5EDE0' }}>
            <img src={t.image} alt={t.name} className="w-full h-[280px] object-cover" />
            <div className="p-6 flex-1 flex flex-col">
              <h4 className="font-serif text-[22px]" style={{ color: '#2D5A3D' }}>{t.name}</h4>
              <p className="mt-3 text-[14px] leading-relaxed flex-1" style={{ color: '#2D5A3D' }}>{t.text}</p>
              <div className="mt-5 flex items-center gap-2">
                <Stars rating={t.rating} size={14} color="#D9A441" />
                <span className="text-[12px] opacity-70" style={{ color: '#2D5A3D' }}>{t.rating}/5</span>
              </div>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const FaqSection = () => (
  <section className="py-20 md:py-28" style={{ backgroundColor: '#F5EDE0' }}>
    <div className="max-w-[820px] mx-auto px-6 md:px-12">
      <div className="mb-10 text-center">
        <p className="text-[13px] uppercase tracking-[0.2em] opacity-70" style={{ color: '#2D5A3D' }}>
          Need help?
        </p>
        <Link to="/pages/contact" className="mt-2 inline-block underline underline-offset-4 text-[14px]" style={{ color: '#2D5A3D' }}>
          Contact our support team
        </Link>
      </div>
      <h2 className="font-serif text-[32px] md:text-[40px] text-center mb-10" style={{ color: '#2D5A3D' }}>
        Frequently Asked Questions
      </h2>
      <Accordion items={HOME_FAQS} />
    </div>
  </section>
);

const Home = () => (
  <main>
    <HeroSection />
    <MarqueeBar />
    <StorySection />
    <QuoteSection />
    <AboutSection />
    <RecognizeSection />
    <TestimonialsSection />
    <FaqSection />
  </main>
);

export default Home;
